import { Button, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { uniqBy } from 'lodash';
import { useEffect, useState } from 'react';

import { Batch, DeclarationStatus, ReportStatus, TemplateType } from '@e-origin/shared';

import { useResetCustomsReport } from '@admin/hooks';

interface ResetCustomsReportProps {
  onClose: () => void;
  batch: Batch;
}

export const ResetCustomsReport: React.FC<ResetCustomsReportProps> = ({ onClose, batch }) => {
  const resetCustomsReportMutation = useResetCustomsReport();
  const [formError, setFormError] = useState<string | null>(null);
  const [alreadyGeneratedLRNs, setAlreadyGeneratedLRNs] = useState<string[]>([]);

  const handleSubmit = async (hardReset = false) => {
    setFormError(null);
    try {
      await resetCustomsReportMutation.mutateAsync({
        hardReset,
        batchId: batch._id,
      });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to reset customs report');
    }
  };

  useEffect(() => {
    const isLv = batch.templateType === TemplateType.LOW_VALUE_H7;
    setAlreadyGeneratedLRNs(
      isLv
        ? []
        : uniqBy(
            (batch.reports?.customsReport?.lrns || [])?.filter(
              (lrnInfo) =>
                (!batch.isExport && lrnInfo.statuses?.some((s) => s.status === DeclarationStatus.RELEASED)) ||
                (batch.isExport && lrnInfo.statuses?.some((s) => s.status === DeclarationStatus.EXPORTED)),
            ),
            (lrn) => lrn.lrn,
          ).map((lrn) => lrn.lrn),
    );
  }, [batch]);

  return (
    <Stack gap="md" mt="xl" align="center">
      <Text>Are you sure you want to reset the customs report?</Text>

      {alreadyGeneratedLRNs.length > 0 && (
        <Stack mt="md" align="center">
          The following LRNs are already done and will be re-generated:
          <ScrollArea h={alreadyGeneratedLRNs.length > 20 ? '400px' : 'fit-content'} offsetScrollbars="y">
            {alreadyGeneratedLRNs.map((lrn) => (
              <Text>{lrn}</Text>
            ))}
          </ScrollArea>
          {[ReportStatus.IN_PROGRESS, ReportStatus.REQUESTED].includes(batch.reports?.customsReport?.status) && (
            <Button w="fit-content" type="button" onClick={() => handleSubmit()} loading={resetCustomsReportMutation.isPending}>
              Just unblock status
            </Button>
          )}
        </Stack>
      )}

      {formError && (
        <Text c="red" fw="bold">
          {formError}
        </Text>
      )}

      <Group justify="center" mt="md">
        <Button type="button" onClick={onClose} disabled={resetCustomsReportMutation.isPending} variant="transparent">
          Cancel
        </Button>
        <Button type="button" onClick={() => handleSubmit(true)} loading={resetCustomsReportMutation.isPending}>
          Reset
        </Button>
      </Group>
    </Stack>
  );
};
